import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { Character, CreateCharacterRequest, UpdateCharacterRequest } from '../../types/character';
import * as characterService from '../../services/characterService';
import { userPreferencesAtom, updateUserPreferencesAtom } from './userPreferencesAtoms';

/**
 * 基础存储原子（内部使用）
 */
const _selectedCharacterIdAtom = atom<string>('default');

/**
 * 存储当前选择的角色卡ID
 * 支持服务器同步，localStorage作为备用
 */
export const selectedCharacterIdAtom = atom(
  // getter: 从用户偏好或localStorage获取
  (get) => {
    const userPreferences = get(userPreferencesAtom);
    if (userPreferences?.selectedCharacterId) {
      return userPreferences.selectedCharacterId;
    }
    // 备用：从localStorage获取
    try {
      const stored = localStorage.getItem('v3_selectedCharacterId');
      return stored ? JSON.parse(stored) : 'default';
    } catch {
      return 'default';
    }
  },
  // setter: 保存到用户偏好和localStorage
  async (get, set, newValue: string) => {
    set(_selectedCharacterIdAtom, newValue);

    // 保存到用户偏好（如果已登录）
    try {
      await set(updateUserPreferencesAtom, { selectedCharacterId: newValue });
    } catch (error) {
      console.warn('保存角色卡设置到用户偏好失败，使用localStorage备用:', error);
      // 备用：保存到localStorage
      try {
        localStorage.setItem('v3_selectedCharacterId', JSON.stringify(newValue));
      } catch (storageError) {
        console.error('保存到localStorage也失败:', storageError);
      }
    }
  }
);



/**
 * 存储我创建的角色卡
 */
export const myCharactersAtom = atom<Character[]>([]);

/**
 * 存储官方角色卡
 */
export const officialCharactersAtom = atom<Character[]>([]);

/**
 * 存储公开角色卡
 */
export const publicCharactersAtom = atom<Character[]>([]);

/**
 * 存储当前选中角色的缓存
 * 用于避免依赖角色列表数组，实现按需加载
 */
export const selectedCharacterCacheAtom = atom<Character | null>(null);

/**
 * 存储按需加载的角色卡缓存
 * 用于缓存消息中引用的角色卡
 */
export const characterCacheMapAtom = atom<Record<string, Character>>({});

/**
 * 是否正在加载角色卡
 */
export const charactersLoadingAtom = atom<boolean>(false);

/**
 * 角色卡加载错误
 */
export const charactersErrorAtom = atom<string | null>(null);



/**
 * 按类型加载角色卡
 */
export const fetchCharactersByTypeAtom = atom(
  null,
  async (get, set, type: 'my' | 'official' | 'public') => {
    try {
      set(charactersLoadingAtom, true);
      set(charactersErrorAtom, null);

      const characters = await characterService.fetchCharactersByType(type);

      // 根据类型更新对应的原子
      switch (type) {
        case 'my':
          set(myCharactersAtom, characters);
          break;
        case 'official':
          set(officialCharactersAtom, characters);
          break;
        case 'public':
          set(publicCharactersAtom, characters);
          break;
      }
    } catch (error) {
      console.error(`Failed to fetch ${type} characters:`, error);
      set(charactersErrorAtom, error instanceof Error ? error.message : 'Unknown error');
    } finally {
      set(charactersLoadingAtom, false);
    }
  }
);

/**
 * 按需加载任意角色卡的详细信息
 */
export const fetchCharacterByIdAtom = atom(
  null,
  async (get, set, characterId: string) => {
    try {
      set(charactersLoadingAtom, true);
      set(charactersErrorAtom, null);

      console.log(`[Character] Loading character by ID: ${characterId}`);

      // 先检查缓存
      const cacheMap = get(characterCacheMapAtom);
      if (cacheMap[characterId]) {
        console.log(`[Character] Using cached character: ${cacheMap[characterId].name}`);
        return cacheMap[characterId];
      }

      // 检查已加载的角色列表
      const myCharacters = get(myCharactersAtom);
      const officialCharacters = get(officialCharactersAtom);
      const publicCharacters = get(publicCharactersAtom);
      const selectedCache = get(selectedCharacterCacheAtom);

      // 从已加载的列表中查找
      const allCharacters = [...myCharacters, ...officialCharacters, ...publicCharacters];
      if (selectedCache && selectedCache.id === characterId) {
        allCharacters.push(selectedCache);
      }

      const existingCharacter = allCharacters.find(char => char.id === characterId);
      if (existingCharacter) {
        // 存入缓存
        set(characterCacheMapAtom, prev => ({
          ...prev,
          [characterId]: existingCharacter
        }));
        console.log(`[Character] Found in loaded lists: ${existingCharacter.name}`);
        return existingCharacter;
      }

      // 如果是默认角色，使用特殊处理
      if (characterId === 'default') {
        if (officialCharacters.length > 0) {
          const defaultCharacter = officialCharacters[0];
          set(characterCacheMapAtom, prev => ({
            ...prev,
            [characterId]: defaultCharacter
          }));
          return defaultCharacter;
        }

        // 加载官方角色列表
        const characters = await characterService.fetchCharactersByType('official');
        set(officialCharactersAtom, characters);

        if (characters.length > 0) {
          const defaultCharacter = characters[0];
          set(characterCacheMapAtom, prev => ({
            ...prev,
            [characterId]: defaultCharacter
          }));
          return defaultCharacter;
        }

        throw new Error('No official characters available as default');
      }

      // 从 API 加载
      const character = await characterService.fetchCharacterById(characterId);

      // 存入缓存
      set(characterCacheMapAtom, prev => ({
        ...prev,
        [characterId]: character
      }));

      console.log(`[Character] Loaded from API: ${character.name}`);
      return character;
    } catch (error) {
      console.error(`Failed to fetch character ${characterId}:`, error);
      set(charactersErrorAtom, error instanceof Error ? error.message : 'Unknown error');
      throw error;
    } finally {
      set(charactersLoadingAtom, false);
    }
  }
);

/**
 * 按需加载选中角色的详细信息
 */
export const fetchSelectedCharacterAtom = atom(
  null,
  async (get, set, characterId: string) => {
    try {
      set(charactersLoadingAtom, true);
      set(charactersErrorAtom, null);

      console.log(`[Character] Loading character by ID: ${characterId}`);

      // 如果是默认角色，尝试获取第一个官方角色
      if (characterId === 'default') {
        // 先检查是否已有官方角色数据
        const officialCharacters = get(officialCharactersAtom);
        if (officialCharacters.length > 0) {
          const defaultCharacter = officialCharacters[0];
          set(selectedCharacterCacheAtom, defaultCharacter);
          console.log(`[Character] Using cached default character: ${defaultCharacter.name}`);
          return defaultCharacter;
        }

        // 如果没有缓存，加载官方角色列表
        console.log('[Character] Loading official characters for default');
        const characters = await characterService.fetchCharactersByType('official');
        set(officialCharactersAtom, characters);

        if (characters.length > 0) {
          const defaultCharacter = characters[0];
          set(selectedCharacterCacheAtom, defaultCharacter);
          console.log(`[Character] Loaded default character: ${defaultCharacter.name}`);
          return defaultCharacter;
        }

        throw new Error('No official characters available as default');
      }

      // 加载指定ID的角色
      const character = await characterService.fetchCharacterById(characterId);
      set(selectedCharacterCacheAtom, character);
      console.log(`[Character] Loaded character: ${character.name}`);

      return character;
    } catch (error) {
      console.error(`Failed to fetch character ${characterId}:`, error);
      set(charactersErrorAtom, error instanceof Error ? error.message : 'Unknown error');
      throw error;
    } finally {
      set(charactersLoadingAtom, false);
    }
  }
);

/**
 * 创建角色卡
 */
export const createCharacterAtom = atom(
  null,
  async (get, set, data: CreateCharacterRequest) => {
    try {
      set(charactersLoadingAtom, true);
      set(charactersErrorAtom, null);

      const newCharacter = await characterService.createCharacter(data);

      // 乐观更新：添加到我的角色卡列表
      const myCharacters = get(myCharactersAtom);
      set(myCharactersAtom, [newCharacter, ...myCharacters]);

      return newCharacter;
    } catch (error) {
      console.error('Failed to create character:', error);
      set(charactersErrorAtom, error instanceof Error ? error.message : 'Unknown error');
      throw error;
    } finally {
      set(charactersLoadingAtom, false);
    }
  }
);

/**
 * 更新角色卡
 */
export const updateCharacterAtom = atom(
  null,
  async (get, set, characterId: string, data: UpdateCharacterRequest) => {
    try {
      set(charactersLoadingAtom, true);
      set(charactersErrorAtom, null);

      const updatedCharacter = await characterService.updateCharacter(characterId, data);

      // 乐观更新：更新我的角色卡列表中的对应项
      const myCharacters = get(myCharactersAtom);
      const updatedMyCharacters = myCharacters.map(char =>
        char.id === characterId ? updatedCharacter : char
      );
      set(myCharactersAtom, updatedMyCharacters);

      return updatedCharacter;
    } catch (error) {
      console.error('Failed to update character:', error);
      set(charactersErrorAtom, error instanceof Error ? error.message : 'Unknown error');
      throw error;
    } finally {
      set(charactersLoadingAtom, false);
    }
  }
);

/**
 * 删除角色卡
 */
export const deleteCharacterAtom = atom(
  null,
  async (get, set, characterId: string) => {
    try {
      set(charactersLoadingAtom, true);
      set(charactersErrorAtom, null);

      await characterService.deleteCharacter(characterId);

      // 乐观更新：从我的角色卡列表中移除
      const myCharacters = get(myCharactersAtom);
      const filteredMyCharacters = myCharacters.filter(char => char.id !== characterId);
      set(myCharactersAtom, filteredMyCharacters);

      // 如果删除的是当前选中的角色卡，切换到默认角色卡
      const selectedCharacterId = get(selectedCharacterIdAtom);
      if (selectedCharacterId === characterId) {
        set(selectedCharacterIdAtom, 'default');
      }
    } catch (error) {
      console.error('Failed to delete character:', error);
      set(charactersErrorAtom, error instanceof Error ? error.message : 'Unknown error');
      throw error;
    } finally {
      set(charactersLoadingAtom, false);
    }
  }
);

/**
 * 检查角色卡是否可用于对话
 * @param character 角色卡对象
 * @param currentUserId 当前用户ID（可选）
 * @returns 是否可用于对话
 */
export const isCharacterAvailableForConversation = (character: Character, currentUserId?: string): boolean => {
  // 官方角色卡总是可用
  if (character.type === 'official') {
    return true;
  }

  // 私有角色卡只有创建者可以使用
  if (character.type === 'private') {
    return character.creatorId === currentUserId;
  }

  // 公开角色卡只有审核通过的才能使用
  if (character.type === 'public') {
    // 创建者可以使用自己的角色卡（即使未审核通过）
    if (character.creatorId === currentUserId) {
      return character.status !== 'rejected';
    }
    // 其他用户只能使用审核通过的角色卡
    return character.status === 'approved';
  }

  return false;
};

/**
 * 角色数据同步检查机制
 * 监听角色ID变化，确保缓存数据与选中ID一致
 */
export const syncSelectedCharacterAtom = atom(
  null,
  async (get, set) => {
    const selectedId = get(selectedCharacterIdAtom);
    const cachedCharacter = get(selectedCharacterCacheAtom);
    const isLoading = get(charactersLoadingAtom);

    // 如果正在加载，跳过同步检查
    if (isLoading) {
      return;
    }

    // 如果缓存中的角色ID与选中ID匹配，无需同步
    if (cachedCharacter && cachedCharacter.id === selectedId) {
      return;
    }

    // 如果选中ID为空或default，且没有缓存，触发默认角色加载
    if ((!selectedId || selectedId === 'default') && !cachedCharacter) {
      console.log('[Character] Syncing default character');
      try {
        await set(fetchSelectedCharacterAtom, 'default');
      } catch (error) {
        console.warn('[Character] Failed to sync default character:', error);
      }
      return;
    }

    // 如果有具体的角色ID但缓存不匹配，触发该角色的加载
    if (selectedId && selectedId !== 'default' && (!cachedCharacter || cachedCharacter.id !== selectedId)) {
      console.log(`[Character] Syncing character: ${selectedId}`);
      try {
        await set(fetchSelectedCharacterAtom, selectedId);
      } catch (error) {
        console.warn(`[Character] Failed to sync character ${selectedId}:`, error);
        // 同步失败时，清空缓存避免显示错误的角色
        set(selectedCharacterCacheAtom, null);
      }
    }
  }
);

/**
 * 根据ID查找角色卡（不触发加载）
 */
export const getCharacterByIdAtom = atom(
  (get) => (characterId: string): Character | null => {
    // 先检查缓存
    const cacheMap = get(characterCacheMapAtom);
    if (cacheMap[characterId]) {
      return cacheMap[characterId];
    }

    // 检查已加载的角色列表
    const myCharacters = get(myCharactersAtom);
    const officialCharacters = get(officialCharactersAtom);
    const publicCharacters = get(publicCharactersAtom);
    const selectedCache = get(selectedCharacterCacheAtom);

    // 从已加载的列表中查找
    const allCharacters = [...myCharacters, ...officialCharacters, ...publicCharacters];
    if (selectedCache && selectedCache.id === characterId) {
      allCharacters.push(selectedCache);
    }

    const existingCharacter = allCharacters.find(char => char.id === characterId);
    if (existingCharacter) {
      return existingCharacter;
    }

    // 如果是默认角色且有官方角色
    if (characterId === 'default' && officialCharacters.length > 0) {
      return officialCharacters[0];
    }

    return null;
  }
);

/**
 * 当前选择的角色卡
 */
export const selectedCharacterAtom = atom(
  get => {
    const selectedId = get(selectedCharacterIdAtom);
    const cachedCharacter = get(selectedCharacterCacheAtom);

    // 如果缓存中有角色且ID匹配，直接返回缓存数据
    if (cachedCharacter && cachedCharacter.id === selectedId) {
      return cachedCharacter;
    }

    // 如果缓存中有角色但ID不匹配，说明用户切换了角色选择
    // 这种情况下返回null，让UI组件触发新的加载
    if (cachedCharacter && cachedCharacter.id !== selectedId) {
      console.log(`[Character] Selected ID changed from ${cachedCharacter.id} to ${selectedId}, cache invalidated`);
      return null;
    }

    // 如果没有缓存数据，尝试从现有的角色列表中查找（向后兼容）
    const myCharacters = get(myCharactersAtom);
    const officialCharacters = get(officialCharactersAtom);
    const publicCharacters = get(publicCharactersAtom);
    const allCharacters = [...myCharacters, ...officialCharacters, ...publicCharacters];

    const selectedCharacter = allCharacters.find(char => char.id === selectedId);
    if (selectedCharacter) {
      // 检查角色卡是否可用
      if (selectedCharacter.type === 'public' && selectedCharacter.status === 'rejected') {
        console.warn(`Selected character ${selectedId} is rejected, falling back to default`);
        return officialCharacters[0] || null;
      }
      return selectedCharacter;
    }

    // 如果是默认角色且有官方角色数据，返回第一个官方角色
    if (selectedId === 'default' && officialCharacters.length > 0) {
      return officialCharacters[0];
    }

    // 没有找到任何匹配的角色，返回null让UI触发加载
    return null;
  }
);

/**
 * 检查并修复不可用的选中角色卡
 */
export const validateSelectedCharacterAtom = atom(
  null,
  async (get, set) => {
    const myCharacters = get(myCharactersAtom);
    const officialCharacters = get(officialCharactersAtom);
    const publicCharacters = get(publicCharactersAtom);
    const selectedId = get(selectedCharacterIdAtom);

    // 合并所有角色卡数据
    const allCharacters = [...myCharacters, ...officialCharacters, ...publicCharacters];

    // 查找选中的角色卡
    const selectedCharacter = allCharacters.find(char => char.id === selectedId);

    // 如果角色卡不存在或不可用，自动切换到默认
    // 注意：这里使用简化的检查，因为在atom中获取当前用户信息比较复杂
    if (!selectedCharacter ||
        (selectedCharacter.type === 'public' && selectedCharacter.status === 'rejected')) {
      console.warn(`Selected character ${selectedId} is not available, switching to default`);
      await set(selectedCharacterIdAtom, 'default');
    }
  }
);

/**
 * 发布到市场原子
 */
export const publishToMarketAtom = atom(
  null,
  async (get, set, { id, allowPromptVisible }: { id: string; allowPromptVisible: boolean }) => {
    try {
      const updatedCharacter = await characterService.publishToMarket(id, allowPromptVisible);

      // 更新本地数据
      const myCharacters = get(myCharactersAtom);
      const updatedCharacters = myCharacters.map(char =>
        char.id === id ? updatedCharacter : char
      );
      set(myCharactersAtom, updatedCharacters);

      return updatedCharacter;
    } catch (error) {
      console.error('Failed to publish to market:', error);
      throw error;
    }
  }
);

/**
 * 撤回审核原子
 */
export const withdrawFromMarketAtom = atom(
  null,
  async (get, set, id: string) => {
    try {
      const updatedCharacter = await characterService.withdrawFromMarket(id);

      // 更新本地数据
      const myCharacters = get(myCharactersAtom);
      const updatedCharacters = myCharacters.map(char =>
        char.id === id ? updatedCharacter : char
      );
      set(myCharactersAtom, updatedCharacters);

      return updatedCharacter;
    } catch (error) {
      console.error('Failed to withdraw from market:', error);
      throw error;
    }
  }
);

/**
 * 下架角色卡原子
 */
export const unpublishCharacterAtom = atom(
  null,
  async (get, set, id: string) => {
    try {
      const updatedCharacter = await characterService.unpublishCharacter(id);

      // 更新本地数据
      const myCharacters = get(myCharactersAtom);
      const updatedCharacters = myCharacters.map(char =>
        char.id === id ? updatedCharacter : char
      );
      set(myCharactersAtom, updatedCharacters);

      return updatedCharacter;
    } catch (error) {
      console.error('Failed to unpublish character:', error);
      throw error;
    }
  }
);
